/* --header-bg: a site's own background (miami: the hero's sky); none set, the
   page's. --header-border: a site's own bottom edge (miami: none, the sky
   runs into the hero); none set, a hairline. */
.header {
  background: var(--header-bg, var(--background));
  border-bottom: var(--header-border, 1px solid var(--border));
  position: relative;
  z-index: 10;
}

/* Over the page's hero (Page `underHeader`): the hero's photo shows through. */
.header.transparent {
  background: none;
  border-bottom: none;
}

/* SITE_UI.headerCorner: a cut-out hanging from the top-left corner (miami: a
   palm frond), over the header's background and under its content (z-index -1
   inside the header's stacking context), past its bottom edge on desktop. */
.corner {
  position: absolute;
  top: 0;
  left: 0;
  z-index: -1;
  width: 44px;
  height: auto;
  pointer-events: none;
  user-select: none;
}

@media (min-width: 1024px) {
  /* Smaller toward 1024 px, where the lockup comes closest to the edge. */
  .corner {
    width: clamp(96px, 9.7vw, 140px);
  }
}

.row {
  max-width: var(--container);
  margin: 0 auto;
  padding: 12px 12px 12px var(--gutter);
  display: flex;
  align-items: center;
  gap: 16px;
  box-sizing: border-box;
}

.lockup {
  display: flex;
  color: var(--text);
  margin-right: auto;
}

.nav {
  display: none;
}

.navLink {
  padding: 12px 0;
  font-size: 14px;
  font-weight: 500;
}

.actions {
  display: flex;
  align-items: center;
  gap: 0;
}

.iconButton {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text);
  cursor: pointer;
  list-style: none;
  /* --header-icon-halo: a glow that keeps the icons (and the language menu)
     legible over the hero's palms under a transparent header; none set, none. */
  filter: var(--header-icon-halo, none);
}

.menu {
  position: relative;
}

.menu > summary::-webkit-details-marker {
  display: none;
}

.langSummary {
  height: 44px;
  padding: 0 10px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  list-style: none;
  filter: var(--header-icon-halo, none);
}

.dropdown {
  position: absolute;
  right: 0;
  top: 100%;
  min-width: 160px;
  padding: 6px 0;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  box-shadow: var(--shadow-card);
  display: flex;
  flex-direction: column;
}

.dropdownLink {
  padding: 11px 18px;
  font-size: 14px;
}

.dropdownLink[aria-current='true'] {
  font-weight: 600;
}

.desktopOnly {
  display: none;
}

/* The phone menu: a full-width sheet over a dimmed page, moving exactly like
   riviera-todo.com's (react-native-web's Modal, animationType "slide"): the
   whole layer, backdrop included, slides up from the bottom in 250 ms and
   back down on close. The page behind it doesn't scroll. */
.layer {
  display: none;
}

.drawer[open] .layer {
  display: block;
  position: fixed;
  inset: 0;
  z-index: 100;
  animation: slideIn 250ms cubic-bezier(0.215, 0.61, 0.355, 1);
}

.drawer[data-closing] .layer {
  animation: slideOut 250ms cubic-bezier(0.47, 0, 0.745, 0.715) forwards;
}

.backdrop {
  position: absolute;
  inset: 0;
  background: rgba(var(--text-rgb), 0.45);
}

.mobileMenu {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  max-height: 100%;
  overflow-y: auto;
  padding: 0 var(--gutter) 20px;
  box-sizing: border-box;
  background: var(--background);
  box-shadow: 0 12px 32px rgba(var(--text-rgb), 0.18);
}

@keyframes slideIn {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0%);
  }
}

@keyframes slideOut {
  from {
    transform: translateY(0%);
  }
  to {
    transform: translateY(100%);
  }
}

@media (prefers-reduced-motion: reduce) {
  .drawer[open] .layer,
  .drawer[data-closing] .layer {
    animation: none;
  }
}

/* Only below the desktop breakpoint, where the drawer is shown: an open one
   left behind by widening the window must not freeze the page. */
@media (max-width: 1023px) {
  :global(html):has(.drawer[open]) {
    overflow: hidden;
  }
}

/* Same height and lockup as the header row, so the sheet reads as the header
   unfolding; the close button sits where the burger was. */
.sheetHeader {
  display: flex;
  align-items: center;
  margin-right: -8px;
  padding: 12px 0;
}

.iconButton:is(button) {
  padding: 0;
  border: 0;
  background: transparent;
}

.mobileNav {
  display: flex;
  flex-direction: column;
  padding: 4px 0 8px;
}

.mobileNav .navLink {
  display: flex;
  align-items: center;
  min-height: 52px;
  padding: 0;
  border-bottom: 1px solid var(--border);
  font-family: var(--font-display);
  font-size: 22px;
  font-weight: 600;
  color: var(--text);
}

.mobileNav .navLink:last-child {
  border-bottom: 0;
}

.mobileLanguages {
  display: flex;
  gap: 22px;
  padding-top: 12px;
  border-top: 1px solid var(--border);
}

.mobileLanguage {
  min-height: 44px;
  display: flex;
  align-items: center;
  font-size: 14px;
  color: var(--text-secondary);
}

.mobileLanguage[aria-current='true'] {
  font-weight: 600;
  color: var(--text);
}

@media (min-width: 1024px) {
  .row {
    padding: 18px var(--gutter);
    gap: 16px 32px;
  }

  .lockup {
    margin-right: 0;
  }

  /* One row from 1280 px in every language (RU: 650 px of labels — keep
     nav labels short); below that long labels wrap inside the nav, the icons
     stay on the logo's row. */
  .nav {
    display: flex;
    flex: 1 1 0;
    min-width: 0;
    flex-wrap: wrap;
    justify-content: center;
    gap: 4px 22px;
  }

  .actions {
    gap: 6px;
  }

  .mobileOnly {
    display: none;
  }

  .desktopOnly {
    display: flex;
  }

  .menu.desktopOnly {
    display: block;
  }
}
